// ============================================
// CATEGORY/MUSCLE CONSTANTS & TRANSLATIONS
// ============================================

/**
 * All known resilience muscle categories
 * These match the category field in the database questions table
 */
export const MUSCLES = [
    "Cognitive Flexibility",
    "Positive Attitude – Self",
    "Focus/Purpose",
    "Positive Attitude – World",
    "Social Flexibility",
    "Initiative",
    "Systematic"
] as const;

export type Muscle = typeof MUSCLES[number];

/**
 * Hebrew translations for each muscle
 */
export const HEBREW_MUSCLES: Record<string, string> = {
    "Cognitive Flexibility": "גמישות חשיבתית",
    "Positive Attitude – Self": "גישה חיובית – עצמי",
    "Focus/Purpose": "מיקוד ותכלית",
    "Positive Attitude – World": "גישה חיובית – עולם",
    "Social Flexibility": "גמישות חברתית",
    "Initiative": "יוזמה",
    "Systematic": "שיטתיות"
};

/**
 * Reverse mapping: Hebrew to English
 */
export const HEBREW_TO_ENGLISH: Record<string, string> = {
    "גמישות מחשבתית": "Cognitive Flexibility",
    "גמישות חשיבתית": "Cognitive Flexibility", // Alternative spelling
    "גישה חיובית – עצמי": "Positive Attitude – Self",
    "גישה חיובית - עצמי": "Positive Attitude – Self", // Handle standard hyphen
    "מיקוד/תכלית": "Focus/Purpose",
    "מיקוד ותכלית": "Focus/Purpose", // Handle DB variation
    "גישה חיובית – עולם": "Positive Attitude – World",
    "גישה חיובית - עולם": "Positive Attitude – World", // Handle standard hyphen
    "גמישות חברתית": "Social Flexibility",
    "יוזמה": "Initiative",
    "שיטתיות": "Systematic"
};

/**
 * Get Hebrew translation for a category, with fallback
 */
export function getHebrewName(category: string): string {
    return HEBREW_MUSCLES[category] || category;
}

/**
 * Get English name from Hebrew, with fallback
 */
export function getEnglishName(hebrewName: string): string {
    return HEBREW_TO_ENGLISH[hebrewName] || hebrewName;
}

/**
 * Global benchmark scores (from population norms)
 * Used for comparison in individual reports
 */
export const GLOBAL_BENCHMARK: Record<string, number> = {
    "Cognitive Flexibility": 6.85,
    "Positive Attitude – Self": 7.19,
    "Focus/Purpose": 6.40,
    "Positive Attitude – World": 6.63,
    "Social Flexibility": 6.06,
    "Initiative": 7.08,
    "Systematic": 5.73
};

/**
 * Color palette for visualizations
 */
export const MUSCLE_COLORS: Record<string, string> = {
    "Cognitive Flexibility": "#6366f1",    // Indigo
    "Positive Attitude – Self": "#22c55e", // Green
    "Focus/Purpose": "#f59e0b",            // Amber
    "Positive Attitude – World": "#06b6d4", // Cyan
    "Social Flexibility": "#ec4899",       // Pink
    "Initiative": "#8b5cf6",               // Violet
    "Systematic": "#64748b"                // Slate
};

/**
 * Short descriptions for each muscle (Hebrew)
 */
export const MUSCLE_DESCRIPTIONS: Record<string, string> = {
    "Cognitive Flexibility": "היכולת לראות מצבים מנקודות מבט שונות, קבלת פרדוקסים",
    "Positive Attitude – Self": "ערך עצמי, מסוגלות עצמית, אמונה ביכולת להשפיע",
    "Focus/Purpose": "הצבת מטרות, מחויבות, הפרדת עיקר מטפל",
    "Positive Attitude – World": "אופטימיות, ראיית הזדמנויות בקושי",
    "Social Flexibility": "שימוש ברשתות תמיכה, נכונות לבקש עזרה",
    "Initiative": "נקיטת פעולה באי-ודאות, לקיחת סיכונים מחושבים",
    "Systematic": "סדר, תכנון, משמעת, ארגון בתוך כאוס"
};
